import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../lib/analytics';

export function LoginPage() {
  const { user, loading, signInWithGoogle, signInWithEmail, createAccountWithEmail, error } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-primary"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={from} replace />;
  }

  const handleGoogleSignIn = async () => {
    setIsSigningIn(true);
    trackEvent('login_started', { method: 'google' });
    try {
      await signInWithGoogle();
    } catch (err) {
      console.error('Sign in failed:', err);
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSigningIn(true);
    trackEvent('login_started', { method: 'email', mode: isCreatingAccount ? 'create_account' : 'sign_in' });
    try {
      if (isCreatingAccount) {
        await createAccountWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (err) {
      console.error('Email sign in failed:', err);
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-12">
          <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-4">
            Welcome to
          </p>
          <h1 className="font-display text-5xl md:text-6xl font-bold lowercase tracking-tight text-foreground mb-4">
            noctua
          </h1>
          <p className="font-body text-lg text-muted-foreground leading-relaxed">
            Text to speech, in your podcast app.
          </p>
        </div>

        <div className="bg-card border border-border rounded-sm p-8 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
          <h2 className="font-display text-2xl font-medium text-foreground mb-6 text-center">
            {isCreatingAccount ? 'Create an account' : 'Sign in to continue'}
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
              <p className="font-body text-sm text-destructive">{error}</p>
            </div>
          )}

          <form onSubmit={handleEmailSubmit} className="space-y-4 mb-6">
            <div>
              <label htmlFor="email" className="block font-body text-sm font-medium text-foreground mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-sm font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 transition-shadow"
                placeholder="you@example.com"
                disabled={isSigningIn}
              />
            </div>
            <div>
              <label htmlFor="password" className="block font-body text-sm font-medium text-foreground mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-background border border-border rounded-sm font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 transition-shadow"
                placeholder="••••••••"
                disabled={isSigningIn}
              />
            </div>
            <button
              type="submit"
              disabled={isSigningIn}
              className="w-full px-6 py-3 bg-foreground text-background border border-foreground rounded-sm font-body font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSigningIn
                ? (isCreatingAccount ? 'Creating account…' : 'Signing in…')
                : (isCreatingAccount ? 'Create account' : 'Sign in')}
            </button>
          </form>

          <p className="text-center font-body text-sm text-muted-foreground mb-6">
            {isCreatingAccount ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              onClick={() => setIsCreatingAccount(!isCreatingAccount)}
              className="text-primary hover:opacity-80 underline transition-opacity"
            >
              {isCreatingAccount ? 'Sign in' : 'Create one'}
            </button>
          </p>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-card font-mono text-xs text-muted-foreground uppercase tracking-widest">or</span>
            </div>
          </div>

          <button
            onClick={handleGoogleSignIn}
            disabled={isSigningIn}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-background text-foreground border border-border rounded-sm font-body font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>Continue with Google</span>
          </button>

          <p className="mt-8 text-center font-body text-sm text-muted-foreground">
            By signing in, you agree to our{' '}
            <a href="https://noctua.uno/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:opacity-80 underline transition-opacity">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="https://noctua.uno/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:opacity-80 underline transition-opacity">
              Privacy Policy
            </a>
          </p>
        </div>

        {/* Editorial detail */}
        <div className="mt-12 pt-8 editorial-rule-thin flex items-center justify-center gap-6 text-muted-foreground font-mono text-xs tracking-wide animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <span>Est. 2026</span>
          <span className="w-1 h-1 rounded-full bg-muted-foreground" />
          <span>Your text → your audio</span>
        </div>
      </div>
    </div>
  );
}
